import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { map } from 'lodash';

import Row from '~/layout/Row';
import Column from '~/layout/Column';

import ButtonGroup from '~/components/base/ButtonGroup';
import Image from '~/components/base/Image';
import BaseLink from '~/components/base/BaseLink';

import { h1, medium, large } from '~/styles/typography';

import { AcfLink, AcfBackgroundColor } from '~/utilities/propTypes';
import { dynamicFontSize, formatText } from '~/utilities/helpers';

const getContentStyles = ({ theme, contentTextBox, contentPlacement }) => {
    let styles = {};
    const desktopStyles = {
        textAlign: contentPlacement,
        alignItems:
            contentPlacement === 'center' ? contentPlacement : 'flex-start'
    };

    if (contentTextBox) {
        styles = {
            backgroundColor: `rgba(${theme.colors.text.blackRgb}, .95)`,
            padding: `25px 50px`
        };
        desktopStyles.padding = `50px 100px`;
    }

    return css({
        ...styles,
        [`@media (min-width: ${theme.breakpoint.largeTablet})`]: {
            ...desktopStyles
        }
    });
};

const getTextColors = {
    title: ({ theme, backgroundColor }) => {
        let styles = {
            color: theme.colors.primary.white
        };
        switch (backgroundColor) {
            case 'lightBlue':
                styles.color = theme.colors.text.red;
                break;
            case 'yellow':
                styles.color = theme.colors.accent.blue;
                break;
            default:
                return css({
                    color: theme.colors.primary.white
                });
        }
        return css({ ...styles });
    },
    copy: ({ theme, backgroundColor }) => {
        switch (backgroundColor) {
            case 'lightBlue':
            case 'yellow':
                return css({
                    color: theme.colors.text.black
                });
            default:
                return css({
                    color: theme.colors.primary.white
                });
        }
    }
};

const getTextSize = {
    title: ({ largeText }) => {
        if (largeText) {
            return css({
                'lineHeight': `1.06`,
                'fontSize': dynamicFontSize(2.8, 8.7, 10.7),
                '@media (min-width: 768px)': {
                    fontSize: dynamicFontSize(5.6, 8.7, 10.7)
                }
            });
        }
    }
};

const Content = styled.div`
    position: relative;
    color: ${({ theme }) => theme.colors.primary.white};
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    ${getContentStyles}
`;

const Title = styled.h1`
    ${h1}
    ${getTextColors.title}
    ${getTextSize.title}
`;

const Copy = styled.p`
    ${getTextColors.copy}
    ${({ largeText }) => (largeText ? large : medium)}
    font-family: ${({ theme }) => theme.fonts.heading};
    font-weight: ${({ theme }) => theme.fontWeights.medium};
    margin-top: 15px;
`;

const AppLinksWrapper = styled.div`
    margin-top: 20px;
    display: flex;
    gap: 10px;
    flex-wrap: wrap;

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        gap: 20px;
    }
`;

const AppImage = styled(Image)`
    max-width: 130px;

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        max-width: 230px;
    }
`;

const HeroContent = ({
    contentPlacement = 'left',
    contentTextBox = false,
    columnSpan = 6,
    largeText = false,
    backgroundColor = 'black',
    title,
    copy = '',
    ctasVariant = 'ctas',
    ctas = [],
    appLinks = [],
    children = null
}) => (
    <>
        <Row>
            <Column
                spanDesktop={columnSpan}
                startDesktop={contentPlacement === 'center' ? 4 : 1}
                spanLargeTablet={8}
                startLargeTablet={contentPlacement === 'center' ? 3 : 1}
                spanTablet={10}
                startTablet={2}
                span={12}
                start={1}
            >
                <Content {...{ contentPlacement, contentTextBox }}>
                    <Title {...{ largeText, backgroundColor }}>
                        {formatText(title)}
                    </Title>
                    {copy ? (
                        <Copy {...{ largeText, backgroundColor }}>
                            {formatText(copy)}
                        </Copy>
                    ) : null}
                    {ctas?.length && ctasVariant === 'ctas' ? (
                        <ButtonGroup
                            links={map(ctas, ({ icon, link }) => ({
                                icon,
                                ...link
                            }))}
                            marginTop="medium"
                        />
                    ) : appLinks?.length && ctasVariant === 'appLinks' ? (
                        <AppLinksWrapper>
                            {appLinks.map(({ linkUrl, badge }) => (
                                <BaseLink to={linkUrl} key={linkUrl}>
                                    <AppImage {...badge} />
                                </BaseLink>
                            ))}
                        </AppLinksWrapper>
                    ) : null}
                </Content>
            </Column>
        </Row>
        {children}
    </>
);

HeroContent.propTypes = {
    contentPlacement: PropTypes.oneOf(['left', 'center', 'right']),
    contentTextBox: PropTypes.bool,
    columnSpan: PropTypes.number,
    largeText: PropTypes.bool,
    ...AcfBackgroundColor,
    title: PropTypes.string.isRequired,
    copy: PropTypes.string,
    ctasVariant: PropTypes.string,
    ctas: PropTypes.arrayOf(
        PropTypes.shape({
            icon: PropTypes.string,
            link: AcfLink
        })
    ),
    appLinks: PropTypes.arrayOf(
        PropTypes.shape({
            linkUrl: PropTypes.string,
            badge: PropTypes.shape({})
        })
    ),
    children: PropTypes.node
};

export default HeroContent;
